/**
 * @fileoverview Resolve the effective default value for a canonical field.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Bridge the template-builder UI (which writes per-field-type defaults into
 *   `field.extras.*`) with the renderer (which historically only read
 *   `field.defaultValue`).
 * - Return a single canonical "default value" any caller can treat as
 *   `field.defaultValue` for purposes of pre-seeding the persist adapter,
 *   firing the FieldRenderer mount-time useEffect, or driving the DateField /
 *   DateTimeField widgets.
 *
 * Dependencies:
 * - `@firesquirrel/core-domain` (canonical types only — pure helper).
 *
 * Architecture:
 * - Pure. No React, no IO, no host imports. Safe to import anywhere.
 * - Source-of-truth for the extras → defaultValue mapping. If a new
 *   field-type default is added to the template builder, extend the switch
 *   here and EVERY renderer surface picks it up automatically.
 *
 * Notes:
 * - `field.defaultValue` ALWAYS wins when set; the extras-based fallback only
 *   applies when defaultValue is null/undefined. This preserves existing test
 *   fixtures and any direct API consumers that already write `defaultValue`.
 * - Sentinel strings (`'today'`, `'now'`) are emitted for date/dateTime so the
 *   existing DateField/DateTimeField sentinel-handling code paths continue to
 *   work without modification.
 */
import type { InspectionFormField } from '@firesquirrel/core-domain';

/**
 * @process resolveFieldDefault
 *
 * Steps:
 * 1. If `field.defaultValue` is non-null, return it as-is (back-compat).
 * 2. Otherwise read `field.extras` for the per-type default convention used
 *    by the template-builder UI:
 *    - toggle: `extras.defaultState` ∈ {'on','off','unset'}
 *    - date:   `extras.defaultToToday` (boolean)
 *    - dateTime: `extras.defaultToNow` (boolean)
 * 3. Return `undefined` when no default can be derived. Callers must treat
 *    `undefined` as "no default configured".
 *
 * Inputs: a canonical `InspectionFormField`.
 * Outputs: the effective default value (`unknown`) or `undefined`.
 *
 * Failure Handling: pure; never throws. Unknown field types return
 *   `field.defaultValue` (which is itself null/undefined when not set).
 * Idempotency: NOT REQUIRED — pure read; calling N times returns the same value.
 */
export function resolveFieldDefault(field: InspectionFormField): unknown {
  // Explicit defaultValue wins. Use loose-null check so an explicit `false`,
  // `0`, or `''` is preserved. Coerce string boolean representations ('false', 'true', '0', '1')
  // for toggle fields to real booleans so JS truthy checks don't evaluate 'false' as true.
  if (field.defaultValue != null) {
    if (field.type === 'toggle' && typeof field.defaultValue === 'string') {
      const normalized = field.defaultValue.trim().toLowerCase();
      if (normalized === 'false' || normalized === '0' || normalized === 'off' || normalized === 'no') return false;
      if (normalized === 'true' || normalized === '1' || normalized === 'on' || normalized === 'yes') return true;
    }
    return field.defaultValue;
  }

  const extras = (field.extras ?? {}) as Record<string, unknown>;
  switch (field.type) {
    case 'toggle': {
      const ds = extras.defaultState;
      if (ds === 'on') return true;
      if (ds === 'off') return false;
      return undefined;
    }
    case 'date':
      return extras.defaultToToday === true ? 'today' : undefined;
    case 'dateTime':
      return extras.defaultToNow === true ? 'now' : undefined;
    default:
      return undefined;
  }
}
